import Vue from 'vue'
import {
  META_TITLE_PARAMS_REGEX,
  META_DESCRIPTION_PARAMS_REGEX,
  META_PARAM,
  NO_DIRECTIVE
} from '@/utils/enums/seo'
import { generatePageScheme } from '@/utils/pageScheme'
import { getLinkToFmBucketFile } from '@/plugins/helper'

const DEFAULT_SEO_SOURCE = 'seo/pageSeo'

const generateMetaTags = function({
  titleParam = null,
  descriptionParam = null,
  schemes = null,
  additionalLinks = [],
  additionalMetaTags = [],
  uncrawlable = false,
  source = DEFAULT_SEO_SOURCE,
  script = [],
  disableSanitizersByTagIdOptions = {},
  canonicalUrl = null,
  ogImageUrl = getLinkToFmBucketFile('fm-new-default-og-img.png')
}) {
  const link = [...additionalLinks]
  const meta = []

  // TODO - DO NOT REMOVE FOR NOW
  // const url = this.$env.DOMAIN_URL + this.$route.path
  // const metaTags = [
  //   { hid: 'description', name: 'description', content: description },
  //   { hid: 'keywords', name: 'keywords', content: null },
  //   {
  //     hid: 'apple-mobile-web-app-title',
  //     name: 'apple-mobile-web-app-title',
  //     content: title
  //   },
  //   { hid: 'og-title', property: 'og:title', content: title },
  //   { hid: 'og-description', property: 'og:description', content: description },
  //   { hid: 'og-type', property: 'og:type', content: 'website' },
  //   { hid: 'og-url', property: 'og:url', content: url },
  //   { hid: 'og-image', property: 'og:image', content: socialInfo.sharingImage },
  //   { hid: 'og-locale', property: 'og:locale', content: 'en_US' },
  //   {
  //     hid: 'og-site-name',
  //     property: 'og:site_name',
  //     content: 'FMPedia - financial news portal'
  //   }
  // ]

  /** Client requirement - to not add canonical links for paginated pages 8+
   *  (means, for main page and 7 first page - we add canonical links,
   *  but for 8+ pages - don't add) **/
  const MAX_PAGE_NUMBER_WITH_ALLOWED_CANONICAL = 7
  const path = this.$route.path.toLowerCase()

  const paginationMatch = path.match(/\/page\/([0-9]*)\/*$/)
  const isPageWithoutPagination = !paginationMatch

  const isCanonicalLinkAllowed =
    isPageWithoutPagination ||
    parseInt(paginationMatch[1]) <= MAX_PAGE_NUMBER_WITH_ALLOWED_CANONICAL

  if (isCanonicalLinkAllowed && canonicalUrl) {
    link.push({
      hid: 'canonical',
      rel: 'canonical',
      href: canonicalUrl
    })
  }

  /**
   *  Generate metatags **/

  const {
    MetaTitle,
    MetaDescription,
    Directives,
    Keyphrases
  } = this.$store.getters[source]

  const title = generateTitle(MetaTitle, titleParam)
  const description = generateDescription(MetaDescription, descriptionParam)
  const ogSiteName = this.$store.getters['seo/ogSiteName']
  const keywords = generateKeywords(Keyphrases)
  const isPreviewUrl = !!this.$route.query.previewId
  const robotDirectives = generateDirectives(
    Directives,
    isPreviewUrl || uncrawlable || !isCanonicalLinkAllowed
  )

  /** Generate article scheme **/
  const pageScheme = generatePageScheme(schemes)

  meta.push(
    ...getMetaSettings(
      this.$helper.getFullPath(this),
      title,
      description,
      keywords,
      robotDirectives,
      ogSiteName,
      ogImageUrl
    ),
    ...additionalMetaTags
  )

  return {
    title,
    link,
    meta,
    __dangerouslyDisableSanitizersByTagID: {
      'page-schemas': ['innerHTML'],
      ...disableSanitizersByTagIdOptions
    },
    script: [...pageScheme.script, ...script]
  }
}

export function generateHomepageTitle(title) {
  return generateTitle(title, META_PARAM.HOMEPAGE)
}

function generateTitle(template, param) {
  return (template || '').replace(META_TITLE_PARAMS_REGEX, param) || param
}

function generateDescription(template, param) {
  return (template || '').replace(META_DESCRIPTION_PARAMS_REGEX, param) || param
}

function generateKeywords(keyphrases) {
  if (!keyphrases || !Array.isArray(keyphrases)) return ''

  return keyphrases.map(({ Keyword }) => Keyword).join(', ')
}

function generateDirectives(str, isNoindex) {
  if (isNoindex) return 'noindex'

  return str === NO_DIRECTIVE ? '' : str.toLowerCase()
}

function generateNoindexNofollowMetaTags({
  titleParam = null,
  descriptionParam = null,
  source = null,
  staticTitle = null,
  staticDescription = null,
  ogImageUrl = getLinkToFmBucketFile('fm-new-default-og-img.png')
} = {}) {
  const { MetaTitle, MetaDescription } = this.$store.getters[
    source || DEFAULT_SEO_SOURCE
  ]
  const meta = [
    {
      hid: 'directives',
      name: 'robots',
      content: 'noindex, nofollow'
    },
    ...generateThumbnailMetaSettings(ogImageUrl)
  ]

  let title

  if (titleParam || staticTitle) {
    title = staticTitle || generateTitle(MetaTitle, titleParam)
  }

  if (descriptionParam || staticDescription) {
    const descriptionMeta = {
      hid: 'description',
      name: 'description',
      content:
        staticDescription ||
        generateDescription(MetaDescription, descriptionParam)
    }

    meta.push(descriptionMeta)
  }

  return {
    ...(title ? { title } : {}),
    meta
  }
}

const createMeta = {
  install(Vue) {
    Vue.prototype.$generateMetaTags = generateMetaTags
    Vue.prototype.$generateNoindexNofollowMetaTags = generateNoindexNofollowMetaTags
  }
}

function generateThumbnailMetaSettings(ogImageUrl) {
  return [
    { hid: 'og-image', property: 'og:image', content: ogImageUrl },
    {
      hid: 'og:image:secure_url',
      property: 'og:image',
      content: ogImageUrl
    },
    {
      hid: 'twitter-image',
      name: 'twitter:image',
      content: ogImageUrl
    }
  ]
}

function getMetaSettings(
  fullPath,
  title,
  description,
  keywords,
  robotDirectives,
  ogSiteName,
  ogImageUrl
) {
  return [
    { hid: 'description', name: 'description', content: description },
    ...(keywords
      ? [{ hid: 'keywords', name: 'keywords', content: keywords }]
      : []),
    { hid: 'directives', name: 'robots', content: robotDirectives },
    {
      hid: 'apple-mobile-web-app-title',
      name: 'apple-mobile-web-app-title',
      content: title
    },
    { hid: 'og-title', property: 'og:title', content: title },
    {
      hid: 'og-description',
      property: 'og:description',
      content: description
    },
    { hid: 'og-type', property: 'og:type', content: 'website' },
    {
      hid: 'og-url',
      property: 'og:url',
      content: fullPath
    },
    // { hid: 'og-locale', property: 'og:locale', content: 'en_US' },
    {
      hid: 'og-site-name',
      property: 'og:site_name',
      content: ogSiteName
    },
    {
      hid: 'twitter-card',
      name: 'twitter:card',
      content: 'summary'
    },
    {
      hid: 'twitter-site',
      name: 'twitter:site',
      content: '@financemagnates'
    },
    {
      hid: 'twitter-title',
      name: 'twitter:title',
      content: title
    },
    {
      hid: 'twitter-description',
      name: 'twitter:description',
      content: description
    },
    ...generateThumbnailMetaSettings(ogImageUrl)
    // {
    //   hid: 'twitter-image-alt',
    //   name: 'twitter:image:alt',
    //   content: metaContent.title
    // }
  ]
}

Vue.use(createMeta)
